import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import { Digibook, getDigibook } from '../api/digibookApi';
import { isDigibookApp, type DigibookApp } from '../utils/digibookAppUtils';
import AppsContext from './AppsContext';
import ModuleContext from './ModuleContext';

type DigibookContextValue = {
  digibook?: Digibook;
  getDigibookAppForModule: (moduleId: string) => DigibookApp | undefined;
};

const DigibookContext = createContext<DigibookContextValue>({
  getDigibookAppForModule: () => undefined,
});

export default DigibookContext;

export function DigibookContextProvider({ children }: PropsWithChildren) {
  const [digibook, setDigibook] = useState<Digibook>();

  const { apps } = useContext(AppsContext);
  const { module, selectedModule } = useContext(ModuleContext);
  const handleError = useThrowAsyncError();

  const digibookApps = useMemo(() => apps.filter(isDigibookApp), [apps]);

  useEffect(() => {
    const fetchDigibook = async () => {
      const currentModule = selectedModule || module;

      if (!currentModule) return;

      try {
        const digibookAppForModule = digibookApps.find(app => app.moduleId === currentModule.id);

        if (!digibookAppForModule || !digibookAppForModule.linkedApp) return setDigibook(undefined);

        const digibook = await getDigibook(digibookAppForModule.linkedApp.id);

        setDigibook(digibook);
      } catch (e) {
        handleError(e);
      }
    };

    fetchDigibook();
  }, [digibookApps, handleError, module, selectedModule]);

  const getDigibookAppForModule = useCallback(
    (moduleId: string) => {
      if (!digibookApps.length) return undefined;
      return digibookApps.find(app => app.moduleId === moduleId);
    },
    [digibookApps],
  );

  return <DigibookContext.Provider value={{ digibook, getDigibookAppForModule }}>{children}</DigibookContext.Provider>;
}
